import { create } from 'zustand';
import { _BULK_IMPORT_CARD_LIMIT, _l2audio, _l2color, _l2image, _l2visibility, _lang } from '../constants';
import { addCardTitleToImagesDictFromArasaacIfNotExistingBulk, deleteEmptyStrings, load, mergeDictsWithPriority, mergeNodes, nodesToUniqueStrings, reorderStringsBasedOnFirstAppearance, sentencesToDict, sleep, store } from '../foo';
import { useUIStore } from './UIZustand';
import { useGlobalStore } from './globalZustand';
import { useTreeStore } from './treeZustand';
import { storyToDict } from '../../foo/text';
import { useUserStore } from './userZustand';

interface ImportState {
    cardsTitleQueue: string[];
    doResetImage: boolean;
    doResetAudio: boolean;
    doResetColor: boolean;
    doResetVisibility: boolean;
    importMode: "tree" | "flat" | "story";
    doImport: (text: string, pathFromURL:string, mergeMode:"add" | "replace") => void;
    finishImport: () => void;
    showOneSentenceAtATime: boolean;
    ignoreNewLines: boolean;

    setCardsTitleQueue: (cardsTitleQueue: string[]) => void;
    setDoResetImage: (doResetImage: boolean) => void;
    setDoResetAudio: (doResetAudio: boolean) => void;
    setDoResetColor: (doResetColor: boolean) => void;
    setDoResetVisibility: (doResetVisibility: boolean) => void;
    setImportMode: (importMode: "tree" | "flat" | "story") => void;
    setShowOneSentenceAtATime: (showOneSentenceAtATime: boolean) => void;
    setIgnoreNewLines: (ignoreNewLines: boolean) => void;

};

const defaultImportState: ImportState = {
    cardsTitleQueue: [],
    doResetImage: false,
    doResetAudio: false,
    doResetColor: false,
    doResetVisibility: false,
    importMode: "story",
    doImport: () => {},
    finishImport: () => {},
    showOneSentenceAtATime: false,
    ignoreNewLines: false,

    setCardsTitleQueue: () => {},
    setDoResetImage: () => {},
    setDoResetAudio: () => {},
    setDoResetColor: () => {},
    setDoResetVisibility: () => {},
    setImportMode: () => {},
    setShowOneSentenceAtATime: () => {},
    setIgnoreNewLines: () => {},
};

export const useImportStore = create<ImportState>()((set, get) => (
        {
    ...defaultImportState,

    setShowOneSentenceAtATime: (showOneSentenceAtATime) => set({ showOneSentenceAtATime: showOneSentenceAtATime }),
    
    // REF-A
    doImport: async (inputText: string, pathFromURL:string, mergeMode:"add" | "replace") => {
        useUIStore.getState().setIsAddOrReplaceDialogOpen(false);       
        const globalState = useGlobalStore.getState();
        globalState.setShowLoading(true);
        const newSentences = deleteEmptyStrings(inputText.split('\n'));
        if(newSentences.length === 0){
            globalState.setShowLoading(false);
        }else{
            const currentState = get();
            const treeState = useTreeStore.getState();
    
            let result;
            let newNodesFromSentences;
            let labelsQueue;
            let newNodes;
    
            if(currentState.importMode === "story"){
                treeState.setNodes({});
                const { showOneSentenceAtATime, ignoreNewLines } = currentState;
                result = storyToDict(pathFromURL, inputText, 4, showOneSentenceAtATime, ignoreNewLines);
                newNodesFromSentences = result.dict;
                labelsQueue = result.labelsQueue;
            }else if(currentState.importMode === "flat"){
                newNodesFromSentences = { [pathFromURL]: newSentences };
                labelsQueue = newSentences;
            }else{   
                newNodesFromSentences = sentencesToDict(newSentences, 4, pathFromURL);
                labelsQueue = reorderStringsBasedOnFirstAppearance(treeState.nodes, nodesToUniqueStrings(newNodesFromSentences));
            }
            if(mergeMode === "add"){
                newNodes = mergeNodes(treeState.nodes, newNodesFromSentences, currentState.importMode === "story");
            }else{
                newNodes = mergeDictsWithPriority(treeState.nodes, newNodesFromSentences, 2);
            }
    
            const userState = useUserStore.getState();
            const { newL2Image, newCachedLocalAutoL2image } = await addCardTitleToImagesDictFromArasaacIfNotExistingBulk(
                userState.userUniqueId,
                userState.userEmail,
                treeState.l2Image,
                labelsQueue.slice(0,_BULK_IMPORT_CARD_LIMIT),
                globalState.lang,
                currentState.doResetImage,
                treeState.cachedLocalAutoL2image);
            await treeState.updateTree(
                newNodes,
                newL2Image,
                newCachedLocalAutoL2image
            );

            globalState.setShowLoading(false);
            set({ cardsTitleQueue: labelsQueue.slice(_BULK_IMPORT_CARD_LIMIT) });
        }
        
    },
    finishImport: async () => {
        let currentState = get();
        if(currentState.cardsTitleQueue.length === 0){
            return;
        }
        
        // Process cardsTitleQueue in batches of _BULK_IMPORT_CARD_LIMIT
        for (let i = 0; i < currentState.cardsTitleQueue.length; i += _BULK_IMPORT_CARD_LIMIT) {
            const batch = currentState.cardsTitleQueue.slice(i, i + _BULK_IMPORT_CARD_LIMIT);
            
            // Get the most recent state
            let treeState = useTreeStore.getState();
            let userState = useUserStore.getState();
            let globalState = useGlobalStore.getState();
    
            const { newL2Image, newCachedLocalAutoL2image } = await addCardTitleToImagesDictFromArasaacIfNotExistingBulk(
                userState.userUniqueId,
                userState.userEmail,
                treeState.l2Image,
                batch,
                globalState.lang,
                currentState.doResetImage,
                treeState.cachedLocalAutoL2image);
            
            await treeState.updateTree(treeState.nodes, newL2Image, newCachedLocalAutoL2image);
            
            // Update currentState for the next iteration
            currentState = get();
            
            await new Promise(resolve => setTimeout(resolve, 650));
        }
    },
    setCardsTitleQueue: (cardsTitleQueue) => set({ cardsTitleQueue: cardsTitleQueue }),
    setDoResetImage: (doResetImage) => set({ doResetImage: doResetImage }),
    setDoResetAudio: (doResetAudio) => set({ doResetAudio }),
    setDoResetColor: (doResetColor) => set({ doResetColor: doResetColor }),
    setDoResetVisibility: (doResetVisibility) => set({ doResetVisibility: doResetVisibility }),
    setImportMode: (importMode: "tree" | "flat" | "story") => {set({ importMode: importMode })},
    setIgnoreNewLines: (ignoreNewLines) => set({ ignoreNewLines: ignoreNewLines }),
    

}));